import { Organisation } from '@/features'
import { isImpersonating } from '@/stores/impersonate'
import { OverallScoreRank, Profile } from '@/types'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { APIClient } from '.'
import { GATSBY_DEV_ENV, IsDevEnv, Settings } from '../app/Settings'
import { IS_SSR } from '../app/constants'
import * as TestPanel from '../app/testPanel'
import { sessionReplayManager } from './amplitudeSessionReplay'

export function isAnalyticsEnabled() {
  const profile = APIClient.Session.QueryKeys.me.getData()
  const sanitizedEmail = profile?.email?.toLowerCase().trim() || ''

  // Exclude Codeleap team members and test users from analytics
  const isCodeleapTeam = sanitizedEmail.endsWith('codeleap')
  const isTestUser = sanitizedEmail.includes('test')

  // Exclude impersonating users, and only enable analytics in production
  const impersonating = isImpersonating()
  const isProduction = TestPanel.isProduction()

  return [
    !impersonating,
    isProduction,
    !isCodeleapTeam,
    !isTestUser,
    !IS_SSR,
    IsDevEnv,
    Settings.EnableAnalytics,
  ].every(Boolean)
}

type User = Profile & {
  badges?: string
  organisationRank?: OverallScoreRank
  score?: Organisation.T.Organisation['score']
  utm?: string
}

type AnalyticsInstance = any

class AnalyticsService {
  private static instance: AnalyticsInstance | null = null

  private static loadingPromise: Promise<AnalyticsInstance> | null = null

  static async init() {
    if (this.instance) return this.instance
    if (this.loadingPromise) return this.loadingPromise

    const writeKey =
      GATSBY_DEV_ENV || TestPanel.isEnabled()
        ? Settings.ApiCredentials.Segment.DEV
        : Settings.ApiCredentials.Segment.PROD

    this.loadingPromise = (async () => {
      const [analytics] = await AnalyticsBrowser.load({ writeKey })

      // @ts-ignore - Expose for debugging + compatibility
      window.analytics = analytics

      const server = GATSBY_DEV_ENV || TestPanel.isEnabled() ? 'development' : 'production'

      // ---- Patch track ----
      const originalTrack = analytics.track.bind(analytics)
      analytics.track = (event, ...args) => {
        if (isImpersonating()) return

        event += '_w'
        args[0] = {
          ...(args?.[0] || {}),
          server,
          ...sessionReplayManager.getCurrentSessionReplayProperties(),
        }
        logger.log(event, args)
        return originalTrack(event, ...args)
      }

      // ---- Patch identify ----
      const originalIdentify = analytics.identify.bind(analytics)
      analytics.identify = ({ user, isLoggedIn }: { user: User; isLoggedIn: boolean }) => {
        if (isImpersonating()) return

        sessionReplayManager.identify(user?.id)

        return originalIdentify(user?.id, {
          id: user?.id,
          email: user?.email,
          address: { country: user?.country },
          company: {
            name: user?.organization?.name,
            id: user?.organization?.id,
            employee_count: user?.organization?.max_employees,
            industry: user?.organization?.category?.label,
          },
          badges: user?.badges,
          gender: user?.gender,
          username: user?.display_name,
          name: user?.name,
          advertising_source: user?.inbound_source,
          climate_priority: user?.climate_priority,
          satisfaction_at_work: user?.satisfaction_at_work,
          date_of_birth: user?.birthday,
          function: user?.department,
          seniority: user?.seniority,
          logged_in: isLoggedIn,
          server,
          climate_empowerment_score: user?.organisationRank?.label,
          climate_empowerment_number: user?.score?.overall,
          head_score: user?.score?.categories[1]?.total,
          heart_score: user?.score?.categories[2]?.total,
          hands_score: user?.score?.categories[3]?.total,
          initial_utm: user?.utm,
        })
      }

      this.instance = analytics
      return analytics
    })()

    return this.loadingPromise
  }

  static get() {
    if (!this.instance) {
      console.warn('[Analytics] Accessed before init(). Returning proxy that will no-op.')
    }
    return this.instance
  }
}

type AnalyticsMethod = 'page' | 'track' | 'identify' | 'reset'

const createAnalyticsProxy = <M extends AnalyticsMethod>(method: M) => {
  return async (...args: Parameters<AnalyticsBrowser[M]>) => {
    if (!isAnalyticsEnabled()) return
    const instance = await AnalyticsService.init()
    return instance[method](...args)
  }
}

export const analytics = {
  page: createAnalyticsProxy('page'),
  track: createAnalyticsProxy('track'),
  identify: createAnalyticsProxy('identify'),
  reset: createAnalyticsProxy('reset'),
}

export const initAnalytics = () => AnalyticsService.init()
